/**
 * 7d / 30d curtailment summary from curtailment_daily (Part 3).
 * Calm, kWh-only; no energy math in UI (values from backend rollups).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sun } from 'lucide-react';
import type { CurtailmentSummary as SummaryShape } from '@/hooks/useCurtailment';

interface CurtailmentSummaryProps {
  summary: SummaryShape;
  loading?: boolean;
  error?: string | null;
}

export function CurtailmentSummary({ summary, loading = false, error = null }: CurtailmentSummaryProps) {
  if (loading && summary.daysWithData === 0) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
            <Sun className="h-4 w-4" />
            Curtailment (7d / 30d)
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="h-8 rounded bg-muted/30 animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (error) return null;

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
          <Sun className="h-4 w-4" />
          Curtailment (7d / 30d)
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="flex items-baseline gap-3 text-sm">
          <span className="text-wt-text-secondary">Last 7 days</span>
          <span className="font-medium tabular-nums">{summary.wasted7dKwh.toFixed(2)} kWh</span>
          <span className="text-wt-text-secondary select-none">·</span>
          <span className="text-wt-text-secondary">Last 30 days</span>
          <span className="font-medium tabular-nums">{summary.wasted30dKwh.toFixed(2)} kWh</span>
        </div>
      </CardContent>
    </Card>
  );
}
